/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  예상 국제배송료 — 배송사 목록 / 응답 해석
 * ════════════════════════════════════════════════════════════════════════════════
 *  배송료비교 화면(UserCompareShipPg / CompareShipPg)과 완전히 같은 규칙을 쓴다.
 *  요금 계산과 각종 제한(선편 20kg·부피제한, K-Packet 2kg, FedEx/UPS 30kg 초과 등)은
 *  전부 백엔드(/shipinfo/checkShippingFee)가 판단해서 내려주므로
 *  프론트엔드는 받은 값을 그대로 출력만 한다. 여기서 다시 계산하면 안 된다.
 *
 *  ※ 이 파일은 앱(toelmobile/src/utils/StoreShipFee.js)과 내용이 동일하다.
 *    한쪽만 고치면 웹과 앱의 안내금액이 달라지므로 반드시 함께 수정할 것.
 * ════════════════════════════════════════════════════════════════════════════════
 */

/** 백엔드가 돌려주는 상태문구 (API 명세서와 동일한 고정값) */
export const SHIP_FEE_UNABLE = 'Unable To Send';   // 발송불가
export const SHIP_FEE_ADMIN = 'Admin Inquiry';     // 관리자문의

/**
 * 출력할 배송사 목록
 *  - key  : checkShippingFee 응답의 필드명
 *  - name : 화면 표기 (배송료비교 화면과 동일)
 *  - days : 평균 소요기간 (API 명세서 [서비스 안내] 의 값과 동일)
 *  순서도 배송료비교 화면과 맞춘다.
 */
export const SHIP_CARRIERS = [
    {key: 'fedEx', name: 'FedEx', days: '1~2'},
    {key: 'fedExEco', name: 'FedEx-E', days: '2~5'},
    {key: 'ups', name: 'UPS', days: '1~2'},
    {key: 'upsEco', name: 'UPS-E', days: '2~5'},
    {key: 'emsPremium', name: 'EMS-Premium', days: '3~4'},
    {key: 'ems', name: 'EMS', days: '3~5'},
    {key: 'kPacket', name: 'K-Packet', days: '6~10'},
    {key: 'seaParcel', name: 'Sea-Parcel', days: '40~70'},
];

/**
 * 응답에서 배송사 1건을 꺼낸다.
 *
 * ⚠ K-Packet 은 키가 두 가지일 수 있다.
 *   Lombok getter 가 getKPacket() 이라 구버전 백엔드는 'kpacket' 으로 직렬화했다.
 *   한쪽만 읽으면 요금이 0원으로 보이므로 둘 다 허용한다.
 *   (배송료비교 화면도 동일하게 처리한다)
 */
export const pickCarrier = (data, key) => {
    if (!data) return null;
    if (key === 'kPacket') return data.kPacket || data.kpacket || null;
    return data[key] || null;
};

/** 실제 금액인가 (= 정상 견적인가) */
export const isShipFeeNumeric = (value) => /^\d+$/.test(String(value ?? ''));

/** 천단위 콤마 (Intl 에 기대지 않는다 — 앱 Hermes 호환) */
export const withCommas = (value) =>
    String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/**
 * 배송사 요금 한 칸에 출력할 문구를 만든다.
 *
 * @param state  'noDimension' 부피/중량 미전달 | 'noCountry' 국가 미선택
 *               | 'loading' 조회중 | 'ready' 조회완료
 * @param value  조회완료일 때 백엔드가 준 finalCost
 * @param t      i18n 번역함수
 * @returns {{text:string, isPrice:boolean}}
 */
export const shipFeeText = (state, value, t) => {
    if (state === 'noDimension') return {text: t('slFeeNoInfo'), isPrice: false};
    if (state === 'noCountry') return {text: t('slFeeNeedCountry'), isPrice: false};
    if (state === 'loading') return {text: t('slFeeLoading'), isPrice: false};

    if (value === SHIP_FEE_UNABLE) return {text: t('slFeeUnable'), isPrice: false};
    if (value === SHIP_FEE_ADMIN) return {text: t('slFeeAdmin'), isPrice: false};
    if (!isShipFeeNumeric(value)) return {text: t('slFeeNoInfo'), isPrice: false};

    return {text: '₩ ' + withCommas(value), isPrice: true};
};

/**
 * 조회 파라미터 만들기
 *  배송료비교 화면과 동일한 매핑을 쓴다.
 *  ⚠ 백엔드 파라미터 이름이 직관과 다르다.
 *     height = 세로(ver) / length = 높이(hei)
 *    여기서 어긋나면 부피중량이 통째로 틀어지므로 반드시 이대로 보낼 것.
 *
 *  [치수 없이 실중량만 있는 경우]
 *   선편(D03)은 부피중량을 쓰지 않고 실무게로만 요금을 매긴다.
 *   그래서 치수를 1cm 로 채워 보내면 둘레/제한부피 조건에 걸리지 않고
 *   순수하게 실무게 기준 요금만 조회된다. (백엔드 buildSeaItem 참고)
 *   나머지 배송사는 이 값으로 계산해도 의미가 없으므로 화면에서 걸러낸다.
 */
export const toShipFeeParams = (countryCode, packing) => {
    if (packing.mode === 'weight') {
        return {countryCode, width: 1, height: 1, length: 1, weight: packing.weight};
    }
    return {
        countryCode,
        width: packing.wid,    // 가로
        height: packing.ver,   // 세로
        length: packing.hei,   // 높이
        weight: packing.weight // 실중량
    };
};

/**
 * 배송사 한 칸의 상태를 정한다.
 *
 * @param mode      모의 패킹 결과 (full | weight | none)
 * @param hasCountry 사용자가 국가를 골랐는가
 * @param loading   조회중인가
 * @param carrierKey 배송사 키
 */
export const feeStateFor = (mode, hasCountry, loading, carrierKey) => {
    if (mode === 'none') return 'noDimension';
    /*
     * 치수 없이 실무게만 있으면 선편 외에는 어떤 국가를 골라도 요금을 낼 수 없다.
     * 국가선택/조회중 판정보다 먼저 걸러야 '조회중…' 이 떴다가 '정보가 없습니다' 로
     * 바뀌는 헛깜빡임이 생기지 않는다.
     */
    if (mode === 'weight' && carrierKey !== 'seaParcel') return 'noDimension';
    if (!hasCountry) return 'noCountry';
    if (loading) return 'loading';
    return 'ready';
};
